/**
 * @deprecated Since version 2.0.0 - Use TanStack Query instead
 * 
 * This file contains legacy NgRx effects that are no longer maintained.
 * 
 * Migration Guide:
 * - For CRUD operations, use TanStack Query mutations:
 *   - Create: `useCreateWarehouse()` or `useCreateUbicacion()`
 *   - Update: `useUpdateWarehouse()` or `useUpdateUbicacion()`
 *   - Delete: `useDeleteWarehouse()` or `useDeleteUbicacion()`
 * - For loading single items: `useWarehouseDetail()` or `useUbicacionDetail()`
 * - Success/error notifications handled in mutation callbacks
 * 
 * Example migration:
 * ```typescript
 * // OLD (NgRx Effect for Delete):
 * delete$ = createEffect(() =>
 *   this.actions$.pipe(
 *     ofType(LocationActionTypes.Delete),
 *     exhaustMap(location => this.service.delete(location.id).pipe(
 *       map(() => new DeleteComplete()),
 *       tap(() => this.alert.success('Eliminado')),
 *       catchError(err => of(new DeleteError(err)))
 *     ))
 *   )
 * );
 * 
 * // NEW (TanStack Query Mutation):
 * deleteMutation = inject(WarehousesQueriesService).useDeleteWarehouse();
 * onDelete(id: number) {
 *   this.deleteMutation.mutate(id, {
 *     onSuccess: () => this.messageService.add({ severity: 'success', detail: 'Eliminado' }),
 *     onError: (err) => this.messageService.add({ severity: 'error', detail: err.message })
 *   });
 * }
 * ```
 * 
 * See documentation: libs/kanzi-location/docs/FASE-1-RESUMEN.md
 */
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { tap, map, exhaustMap, catchError } from 'rxjs/operators';
import { LocationService } from '@kanzi-apes/kanzi-locations';
import { AlertService, AlertVoiceService } from '@kanzi-apes/kanzi-alerts';
import {
  LocationModel,
  LocationCreateModel,
  LocationCreateModelFile,
} from '@kanzi-apes/kanzi-models';

import {
  LoadListError,
  LoadLocation,
  LoadLocationComplete,
  LocationActionTypes,
  Create,
  CreateComplete,
  CreateError,
  Delete,
  DeleteComplete,
  DeleteError,
  Edit,
  EditComplete,
  EditError,
  LoadMasterFile,
  LoadMasterFileComplete,
  LoadMasterFileError,
} from '../actions';
import { toFormData } from '@kanzi-apes/kanzi-utils';
import { Router } from '@angular/router';

/**
 * @deprecated Use TanStack Query services instead
 */
@Injectable()
export class LocationEffects {
  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActionTypes.LoadLocation),
      map((action: LoadLocation) => action.payload),
      exhaustMap((id: number) =>
        this.locationService.getlocation(id).pipe(
          map((response) => new LoadLocationComplete(response)),
          catchError((error) => of(new LoadListError(error)))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActionTypes.Delete),
      map((action: Delete) => action.payload),
      exhaustMap((location: LocationModel) => {
        return this.locationService
          .deleteLocation(location && location.id ? location.id : null)
          .pipe(
            map((response) => new DeleteComplete()),
            catchError((error) => of(new DeleteError(error)))
          );
      })
    )
  );

  deleteComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.DeleteComplete),
        tap(() =>
          this.alertService.success('La locación se eliminó exitosamente.')
        )
      ),
    { dispatch: false }
  );

  deleteError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.DeleteError),
        tap((action: DeleteError) => {
          this.alertService.error(action.payload);
        })
      ),
    { dispatch: false }
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActionTypes.Create),
      map((action: Create) => action.payload),
      exhaustMap((location: LocationCreateModel) =>
        this.locationService.createlocation(location).pipe(
          map((response) => new CreateComplete(response)),
          catchError((error) => of(new CreateError(error)))
        )
      )
    )
  );

  createMasterFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActionTypes.LoadMasterFile),
      map((action: LoadMasterFile) => action.payload),
      exhaustMap((location: LocationCreateModelFile) =>
        this.locationService.createlocationFile(toFormData(location)).pipe(
          map((response) => new LoadMasterFileComplete(response)),
          catchError((error) => of(new LoadMasterFileError(error)))
        )
      )
    )
  );

  createComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LocationActionTypes.CreateComplete,
          LocationActionTypes.LoadMasterFileComplete
        ),
        tap((action: CreateComplete) => {
          if (action.type == LocationActionTypes.CreateComplete) {
            const link = {
              path: 'configurations/location/detail/',
              param: action.payload.id,
            };
            this.alertService.success(
              'La locación se creó exitosamente.',
              link
            );
          } else if (
            action.type == LocationActionTypes.LoadMasterFileComplete
          ) {
            this.alertVoiceService.success({
              msg: 'La locaciones se crearon exitosamente.',
              title: 'locaciones creadas',
            });
            this.router.navigate(['configurations/location/list']);
          }
        })
      ),
    { dispatch: false }
  );

  loadListError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.LoadListError),
        tap((action: LoadListError) => {
          this.alertService.error(action.payload);
        })
      ),
    { dispatch: false }
  );

  CreateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.CreateError),
        tap((action: CreateError) => {
          this.alertService.error(action.payload);
        })
      ),
    { dispatch: false }
  );

  EditLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActionTypes.Edit),
      map((action: Edit) => action.payload),
      exhaustMap((location: LocationCreateModel) =>
        this.locationService.editLocation(location).pipe(
          map((response) => new EditComplete(response)),
          catchError((error) => of(new EditError(error)))
        )
      )
    )
  );

  editComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.EditComplete),
        tap((action: EditComplete) => {
          const link = {
            path: 'configurations/location/detail/',
            param: action.payload.id,
          };
          this.alertService.success('La locación se Edito exitosamente.', link);
        })
      ),
    { dispatch: false }
  );

  EditError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActionTypes.EditError),
        tap((action: EditError) => {
          this.alertService.error(action.payload);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private locationService: LocationService,
    private alertService: AlertService,
    private alertVoiceService: AlertVoiceService,
    private router: Router
  ) {}
}
